import React from "react";
import { createRoot } from "react-dom/client";
import { MantineProvider } from "@mantine/core";
import { ChatProvider } from "../contexts/ChatContext";
import NewWindowChat from "../NewWindowChat";

export class NewWindowChatManager {
  constructor() {
    this.windows = new Map(); // Track multiple windows by ID
    this.roots = new Map(); // Track React roots for cleanup
    this.windowCounter = 0;
  }

  copyStylesToWindow(targetWindow) {
    if (!targetWindow?.document?.head || !document?.head) return;

    const styleNodes = document.head.querySelectorAll(
      'link[rel="stylesheet"], style',
    );

    styleNodes.forEach((node) => {
      if (node.tagName === "LINK") {
        const href = node.getAttribute("href");
        if (!href) return;

        const link = targetWindow.document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;

        const media = node.getAttribute("media");
        if (media) link.media = media;

        const integrity = node.getAttribute("integrity");
        if (integrity) link.integrity = integrity;

        const crossOrigin = node.getAttribute("crossorigin");
        if (crossOrigin) link.crossOrigin = crossOrigin;

        const referrerPolicy = node.getAttribute("referrerpolicy");
        if (referrerPolicy) link.referrerPolicy = referrerPolicy;

        targetWindow.document.head.appendChild(link);
        return;
      }

      if (node.tagName === "STYLE") {
        const style = targetWindow.document.createElement("style");
        style.textContent = node.textContent;
        targetWindow.document.head.appendChild(style);
      }
    });
  }

  // Create a new chat window with React component
  createWindow(chatService, chatContext, options = {}) {
    const windowId = ++this.windowCounter;
    const windowFeatures =
      options.features ||
      "width=400,height=600,resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no";

    const newWindow = window.open("", `ChatWindow_${windowId}`, windowFeatures);

    if (!newWindow) {
      console.error(
        `Failed to create new window ${windowId} - popup blocked or failed`,
      );
      return null;
    }

    // Store window reference
    this.windows.set(windowId, newWindow);

    // Set up the basic HTML structure with Tailwind CSS
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>lawyal.ai Chat</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: 'Lexend', sans-serif; height: 100vh; overflow: hidden; 
      background-color: #ffffff;
    }
    #chat-root { 
      height: 100vh; 
      width: 100vw;
      display: flex;
      flex-direction: column;
    }
  </style>
</head>
<body>
  <div id="chat-root" class="h-full w-full flex flex-col bg-white"></div>
</body>
</html>`;

    try {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      this.copyStylesToWindow(newWindow);
    } catch (error) {
      console.error(`Failed to write HTML to window ${windowId}:`, error.message);
      return null;
    }

    // Wait for the DOM to be ready, then render React
    const renderReactApp = () => {
      const rootElement = newWindow.document.getElementById("chat-root");
      if (rootElement) {
        const root = createRoot(rootElement);

        // Store the root for cleanup
        this.roots.set(windowId, root);

        // Clone the current chat state for the new window with proper mode
        const initialState = {
          messages: [...chatContext.messages],
          conversationID: chatContext.conversationID,
          initialSuggestions: chatContext.initialSuggestions,
          fileIds: chatContext.fileIds, // Include fileIds
          inputDraft: chatContext.inputDraft,
          mode: "sidebar", // Ensure new windows start in sidebar mode, not closed
          loading: false,
          error: null,
        };

        // Render the NewWindowChat component with its own ChatProvider
        // We'll store initial props so we can re-render with updated messages
        const initialProps = {
          messages: initialState.messages,
          inputDraft: initialState.inputDraft,
          loading: false,
          error: null,
          conversationID: initialState.conversationID,
        };

        // Create handler props from chatService
        const handlerProps = chatService
          ? {
              onSend: (text) => chatService.sendMessage(text),
              onSuggestionClick: (sug, conv) =>
                chatService.handleSuggestionClick(sug, conv),
              onCopy: (txt) => chatService.copyMessage(txt),
              onRerun: (txt, conv) => chatService.rerunMessage(txt, conv),
              updateInputDraft: (d) => chatService.updateInputDraft(d),
            }
          : {};

        try {
          root.render(
            <MantineProvider>
              <ChatProvider initialState={initialState}>
                <NewWindowChat {...initialProps} {...handlerProps} />
              </ChatProvider>
            </MantineProvider>,
          );

          // Store current props to allow updates
          this.windowsProps = this.windowsProps || new Map();
          this.windowsProps.set(windowId, initialProps);
        } catch (error) {
          console.error(
            `Failed to render React app in window ${windowId}:`,
            error.message,
          );
          this.cleanupWindow(windowId);
          return null;
        }
        this.roots.set(windowId, root);
        // store chatService reference if provided
        this.chatServices = this.chatServices || new Map();
        if (chatService) this.chatServices.set(windowId, chatService);

        // Set up synchronization with parent context
        this.setupContextSync(windowId, chatContext);
      }
    };

    // Render immediately or wait for document ready
    if (newWindow.document.readyState === "complete") {
      renderReactApp();
    } else {
      newWindow.addEventListener("load", renderReactApp);
    }

    // Focus the new window
    newWindow.focus();

    // Set up cleanup when window closes
    const checkClosed = () => {
      if (newWindow.closed) {
        this.cleanupWindow(windowId);
      } else {
        setTimeout(checkClosed, 1000);
      }
    };
    setTimeout(checkClosed, 1000);

    return windowId;
  }

  // Set up bidirectional synchronization between parent and child contexts
  setupContextSync(windowId, parentContext) {
    const window = this.windows.get(windowId);
    if (!window) return;

    // Save parentContext reference for later updates
    this.parentContexts = this.parentContexts || new Map();
    this.parentContexts.set(windowId, parentContext);

    // Immediately send current messages as props
    this.updateWindow(windowId, { messages: parentContext.messages });
  }

  // Update all open windows (called from ChatManager when messages or other state change)
  // `updates` is an object of props to merge into each window (e.g. { messages, inputDraft })
  updateAllWindows(updates) {
    if (!updates) return;

    const activeWindowCount = this.getActiveWindowCount();
    if (activeWindowCount > 0) {
      this.windows.forEach((win, windowId) => {
        this.updateWindow(windowId, updates);
      });
    }
  }

  // Update a single window's props and re-render the component
  updateWindow(windowId, updates = {}) {
    const root = this.roots.get(windowId);
    const win = this.windows.get(windowId);

    if (!root || !win || win.closed) {
      return;
    }

    try {
      const currentProps =
        (this.windowsProps && this.windowsProps.get(windowId)) || {};
      const newProps = { ...currentProps, ...updates };
      this.windowsProps.set(windowId, newProps);

      const parentCtx =
        this.parentContexts && this.parentContexts.get(windowId);
      const chatService = this.chatServices && this.chatServices.get(windowId);

      const initialState = parentCtx
        ? {
            messages: newProps.messages || [],
            conversationID: parentCtx.conversationID,
            initialSuggestions: parentCtx.initialSuggestions,
            fileIds: parentCtx.fileIds, // Include fileIds
            inputDraft: newProps.inputDraft || "",
            mode: "sidebar", // Ensure mode stays consistent
            loading: newProps.loading || false,
            error: newProps.error || null,
          }
        : {
            messages: newProps.messages || [],
            mode: "sidebar",
            loading: false,
            error: null,
          };

      const handlerProps = {
        onSend: chatService
          ? (text) => chatService.sendMessage(text)
          : undefined,
        onSuggestionClick: chatService
          ? (sug, conv) => chatService.handleSuggestionClick(sug, conv)
          : undefined,
        onCopy: chatService ? (txt) => chatService.copyMessage(txt) : undefined,
        onRerun: chatService
          ? (txt, conv) => chatService.rerunMessage(txt, conv)
          : undefined,
        updateInputDraft: chatService
          ? (d) => chatService.updateInputDraft(d)
          : undefined,
      };

      root.render(
        <MantineProvider>
          <ChatProvider initialState={initialState}>
            <NewWindowChat {...newProps} {...handlerProps} />
          </ChatProvider>
        </MantineProvider>,
      );
    } catch (error) {
      console.error(`Failed to update window ${windowId}:`, error.message);
      // Don't cleanup on update errors, just log them
    }
  }

  // Clean up a specific window
  cleanupWindow(windowId) {
    const window = this.windows.get(windowId);
    const root = this.roots.get(windowId);

    // Unmount React root
    if (root) {
      try {
        root.unmount();
        this.roots.delete(windowId);
      } catch (error) {
        console.error(
          `Error unmounting React root for window ${windowId}:`,
          error.message,
        );
      }
    }

    // Close window if still open
    if (window && !window.closed) {
      try {
        window.close();
      } catch (error) {
        console.error(`Error closing window ${windowId}:`, error.message);
      }
    }

    // Clean up all references
    this.windows.delete(windowId);
    if (this.windowsProps) this.windowsProps.delete(windowId);
    if (this.parentContexts) this.parentContexts.delete(windowId);
    if (this.chatServices) this.chatServices.delete(windowId);
  }

  // Close all windows and cleanup
  closeAllWindows() {
    this.windows.forEach((window, windowId) => {
      this.cleanupWindow(windowId);
    });
  }

  // Get count of active windows
  getActiveWindowCount() {
    let count = 0;
    this.windows.forEach((window) => {
      if (window && !window.closed) {
        count++;
      }
    });
    return count;
  }

  // Check if any windows are open
  hasActiveWindows() {
    return this.getActiveWindowCount() > 0;
  }
}

// Singleton instance for global use
export const newWindowChatManager = new NewWindowChatManager();

// Hook for React components to use the manager
export function useNewWindowChatManager() {
  return newWindowChatManager;
}
